<template>
  <Component
    :is="activeConfig.component"
    :get-first-page="activeConfig.getFirstPage"
    :get-next-page="activeConfig.getNextPage"
    :groq-params="{
      normalizedTerm: normalizedSearchTerm,
      term: standardSearchTerm,
    }"
    :grid-class="
      searchType === 'editorial'
        ? {
            md: false,
            sm: true,
          }
        : undefined
    "
  >
    <template #header="{ notFound }">
      <header v-if="!notFound" class="mb-4">
        <h1 class="text-sm font-bold uppercase">
          Search results for {{ searchTerm }}
        </h1>
      </header>
    </template>
    <template #not-found>
      No results found. Please refine your search and try again.
    </template>
  </Component>
</template>

<script setup lang="ts">
import { OpportunitiesFeed, EditorialFeed, BrandsFeed } from "#components"
import { searchByTerm as searchByOpportunitiesTerm } from "~/groq/opportunities"
import { searchByTerm as searchByEditorialTerm } from "~/groq/editorial"
import { searchByTerm as searchByBrandTerm } from "~/groq/brands"
import { DEFAULT_SEARCH_TYPE } from "~/config/defaults"

const {
  getFirstPage: getFirstOpportunitiesPage,
  getNextPage: getNextOpportunitiesPage,
} = searchByOpportunitiesTerm()

const {
  getFirstPage: getFirstEditorialPage,
  getNextPage: getNextEditorialPage,
} = searchByEditorialTerm()

const { getFirstPage: getFirstBrandsPage, getNextPage: getNextBrandsPage } =
  searchByBrandTerm()

const route = useRoute()

const searchType = ref<string>(
  (route.query.type as string) ?? DEFAULT_SEARCH_TYPE,
)
const searchTerm = ref<string>((route.query.s as string) ?? "")

type Config = {
  component: ReturnType<typeof defineComponent>
  getFirstPage: () => string
  getNextPage: () => string
}

const typeConfig: Record<string, Config> = {
  opportunities: {
    component: OpportunitiesFeed,
    getFirstPage: getFirstOpportunitiesPage,
    getNextPage: getNextOpportunitiesPage,
  },
  editorial: {
    component: EditorialFeed,
    getFirstPage: getFirstEditorialPage,
    getNextPage: getNextEditorialPage,
  },
  brands: {
    component: BrandsFeed,
    getFirstPage: getFirstBrandsPage,
    getNextPage: getNextBrandsPage,
  },
}

const activeConfig = computed(
  () => typeConfig[searchType.value] ?? typeConfig[DEFAULT_SEARCH_TYPE],
)

const normalizedSearchTerm = computed(() => {
  const normalized = searchTerm.value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[aeiou]/gi, "*")
    .toLowerCase()

  return normalized + (normalized.length > 2 ? "*" : "")
})

const standardSearchTerm = computed(
  () => searchTerm.value + (searchTerm.value.length > 2 ? "*" : ""),
)

definePageMeta({
  key: (route) => route.fullPath,
})
</script>
